import httpRequestHandler, {
    CancelToken,
    isCancel,
} from '../utils/httpRequestHandler';
import cacheService from './cacheService';

class InsightQoreService {
    /** @typedef {import('../services/cacheService').CacheService} CacheService */

    /** @type {CacheService} */
    #cacheService;

    #cancelTokens = [];

    /**
     * @param {CacheService} cacheService
     */
    constructor(cacheService) {
        this.#cacheService = cacheService;
    }

    setAxiosInterceptors = ({ onLogout }) => {
        httpRequestHandler.interceptors.response.use(
            response => response,
            error => {
                if (error.response && error.response.status === 401) {
                    this.setSession(null);

                    if (onLogout) {
                        onLogout();
                    }
                }

                return Promise.reject(error);
            }
        );
    };

    setSession = accessToken => {
        if (accessToken) {
            localStorage.setItem('accessToken', accessToken);
            httpRequestHandler.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
            localStorage.removeItem('accessToken');
            delete httpRequestHandler.defaults.headers.common.Authorization;
        }
    };

    getAccessToken = () => localStorage.getItem('accessToken');

    getCommissionVouchersBySalesPerson = async (
        salesperson,
        month,
        year,
        override = null,
        channel = null
    ) => {
        const { data } = override
            ? await httpRequestHandler.get(
                `/commission-voucher/commission-report/${salesperson}/${month}/${year}?override=${override}&channel=${channel}`
            )
            : await httpRequestHandler.get(
                `/commission-voucher/commission-report/${salesperson}/${month}/${year}?channel=${channel}`
            );
        return data;
    };

    getGetTieredCommissionSummaryBySalesRep = async (
        salesperson,
        month,
        year,
        atQuota,
        yoY
    ) => {
        if (!salesperson) return [];
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/tiered-commission-summary/${salesperson}/${month}/${year}?atquota=${atQuota}&yoy=${yoY}`
        );
        return data;
    };

    getCommissionVouchersDataWithSalesPeriodsAndSalesMembers = async (
        view,
        teamMember
    ) => {
        let result = [];
        if (teamMember) {
            let { data } = await httpRequestHandler.get(
                `/commission-voucher/commission-report-sales-periods/${view}/${teamMember}`
            );
            result = data;
        } else {
            let { data } = await httpRequestHandler.get(
                `/commission-voucher/commission-report-sales-periods/${view}`
            );
            result = data;
        }

        return result;
    };

    getRoofTopSales = async (roofTopId, month, year) => {
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/rooftop-sales/${roofTopId}/${month}/${year}`
        );
        return data;
    };

    getRoofTopSalesBde = async (roofTopId, month, year, salesPerson) => {
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/rooftop-sales-bde/${roofTopId}/${month}/${year}?salesperson=${salesPerson}`
        );
        return data;
    };

    getRoofTopSalesByProduct = async (roofTopId, month, year) => {
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/rooftop-sales-by-product/${roofTopId}/${month}/${year}`
        );
        return data;
    };

    getCommissionHistory = async (salesPersonName, month, year) => {
        if (salesPersonName === undefined || salesPersonName === '' || month === undefined || year === undefined) return;
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/commission-history/${salesPersonName}/${month}/${year}`
        );
        return data;
    };

    getLateRemitSummary = async (salesPersonName, month, year) => {
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/late-remittance-payments/${salesPersonName}/${month}/${year}`
        );
        return data;
    };

    updateCommission = async adjustment => {
        const { data } = await httpRequestHandler.post(
            '/commission-voucher/commission-adjustment',
            adjustment
        );
        return data;
    };

    //#region Sales Team Management
    getTeamMemberCommissionTierDrafts = async (salesTeamId) => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/tier-date-range/${salesTeamId}`,
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    setTierDateRange = async ({ salesTeamId, startDate, endDate, salesCommissionTierRangeId }) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/set-tier-date-range',
                { salesTeamId, startDate, endDate, salesCommissionTierRangeId }
            );

            return res.data?.salesCommissionTierRangeId || null;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    cloneTiersToDraft = async (clonedTiers) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/clone-tiers-to-draft',
                clonedTiers
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    deleteCommissionTierDraft = async (selectedCommissionTierDraft) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/remove-tier-date-range',
                selectedCommissionTierDraft
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    publishCommissionTierDraft = async (selectedCommissionTierDraft) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/publish-draft-tiers',
                selectedCommissionTierDraft
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getSalesAgentCommissionTiersById = async teamMemberId => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/commission-tiers/${teamMemberId}`
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
        }
    };

    getSalesAgentCommissionTiers = async name => {
        let res = await httpRequestHandler.get(
            `/sales-team-management/sales-commission-tier/${name}`
        );
        return res.data;
    };

    getTeamMemberDraftTiers = async (salesTeamId, salesCommissionTierRangeId) => {
        if (salesTeamId === undefined || salesCommissionTierRangeId === undefined) return [];
        if (salesTeamId === '' || salesCommissionTierRangeId === '') return [];
        if (salesTeamId === -1 || salesCommissionTierRangeId === -1) return [];
        try {

            let res = await httpRequestHandler.get(
                `/sales-team-management/draft-tiers/${salesTeamId}/${salesCommissionTierRangeId}`,
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getAssociatedMembers = async (fullName, buildHierarchy = false) => {
        const { data } = await httpRequestHandler.get(
            `/sales-team-management/asr-associate-fullname/${fullName}?buildHierarchy=${buildHierarchy}`
        );
        return data;
    };

    getSalesAgentRoles = async name => {
        let res = await httpRequestHandler.get(
            '/sales-team-management/sales-roles'
        );
        return res.data;
    };



    memberHasCommissionData = async teamMemberName => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/has-commission/${teamMemberName}`
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
        }
    };

    changeTiers = async (teamMemberId, tier) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/change-tier',
                { teamMemberId, tier }
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
        }
    };



    getSalesTeamMemeberAttributeByFullName = async (teamMemberFullName, month, year) => {
        try {
            if (teamMemberFullName === null) return [];
            if (teamMemberFullName === undefined || teamMemberFullName == '') return [];

            let res = null;
            if (month != null && year != null) {
                res = await httpRequestHandler.get(`/sales-team-management/team-member-attribute/${teamMemberFullName}/${month}/${year}`)
            } else {
                res = await httpRequestHandler.get(`/sales-team-management/team-member-attribute/${teamMemberFullName}`);
            }
            return res?.data;
        } catch (error) {
            console.log(error.message);
        }
    };

    getAssociatedTeamMembersByTeamMemberId = async teamMemberId => {

        if (teamMemberId === undefined) return [];
        let res = await httpRequestHandler.get(
            `/sales-team-management/associates/${teamMemberId}`
        );
        return res.data;

    };

    getAssociatedTeamMemberById = async teamMemberId => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/asr-associate/${teamMemberId}`
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getSalesTeamAttributes = async name => {
        let res = await httpRequestHandler.get(
            '/sales-team-management/team-member'
        );
        if (name) {
            res = await httpRequestHandler.get(
                `/sales-team-management/team-member/${name}`
            );
        }
        return res.data;
    };

    getSalesAgentCommissionOverrides = async childId => {
        let res = await httpRequestHandler.get(
            `/sales-team-management/sales-commission-overrides/${childId}`
        );
        return res.data;
    };

    createUpdateSalesTeamAttributes = async teamMemberData => {
        let res = await httpRequestHandler.post(
            '/sales-team-management/sales-agent',
            teamMemberData
        );

        return res.data;
    };

    createUpdateCommissionAdjustment = async commissionAdjustments => {
        let res = await httpRequestHandler.post(
            '/commission-voucher/commission-adjustment',
            commissionAdjustments
        );

        return res.data;
    };

    createUpdateManagerSalesReps = async (managerId, assignedSalesReps) => {
        let res = await httpRequestHandler.post('/sales-team-management/manager-rep-assignment', { managerId, assignedSalesReps });

        return res.data;
    };


    getCommissionAdjustments = async (salesTeamId, month, year) => {
        let res = await httpRequestHandler.get(
            `/commission-voucher/commission-adjustment/${salesTeamId}/${month}/${year}`,
        );

        return res.data;
    };

    createUpdateTeamMemberTier = async tierData => {
        let res = await httpRequestHandler.post(
            '/sales-team-management/sales-commission-tier',
            tierData
        );

        return res.data;
    };

    createUpdateTeamMemberDraftTier = async tierData => {
        let res = await httpRequestHandler.post(
            '/sales-team-management/draft-tiers',
            tierData
        );

        return res.data;
    };

    removeAssociatedTeamMember = async teamMemberData => {
        let res = await httpRequestHandler.delete(
            `/sales-team-management/asr-associate/${teamMemberData.parentSalesTeamId}/${teamMemberData.childSalesTeamId}`
        );

        return res.data;
    };

    removeTeamMemberTier = async tierData => {
        let res = await httpRequestHandler.delete(
            `/sales-team-management/sales-commission-tier/${tierData.salesCommissionTierDraftId}/${tierData.salesTeamId}`
        );

        return res.data;
    };

    getDraftTiers = async (salesTeamId, salesCommissionTierRangeId) => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/draft-tiers/${salesTeamId}/${salesCommissionTierRangeId}`,
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };
    //#endregion

    //#region Monthly Territory Review

    getMtrRevenueSummary = async (salesperson, month, year) => {
        if (salesperson === undefined) return [];
        try {
            const { data } = await httpRequestHandler.get(
                `/monthly-territory-review/revenue-mgr-view/${salesperson}/${year}/${month}`
            );
            return data;
        } catch (error) {
            return [];
        }
    };

    getMtrReportData = async (
        reportType,
        salesperson,
        month,
        year,
        view = '',
        viewId = '',
        agentId = '',
        forecast = '',
        productNumber = '',
        summaryType = '',
        channel = '',
        isCroView = false
    ) => {

        if (salesperson === undefined || salesperson === '') return [];
        if (Array.isArray(salesperson) && salesperson.length === 0) {
            return [];
        }
        let uri = `/monthly-territory-review/${reportType}/${salesperson}/${year}/${month}?view=${view}&viewId=${viewId}&agentId=${agentId}`;
        if (forecast && forecast !== '') {
            uri += `&forecast=${forecast}`;
        }
        if (productNumber && productNumber !== '') {
            uri += `&productNumber=${productNumber}`;
        }
        if (summaryType && summaryType !== '') {
            uri += `&summaryType=${summaryType}`;
        }
        if (channel && channel !== '') {
            uri += `&channel=${channel}`;
        }

        uri += `&isCroView=${isCroView}`;

        try {
            const { data } = await httpRequestHandler.get(
                uri
            );
            return data;
        } catch (error) {
            return [];
        }
    };

    getMtrReportDataByProductViewFirst = async (mtrReportRequest, isCroView = false) => {

        if (mtrReportRequest.salesPerson === undefined || mtrReportRequest.salesPerson === '') return [];
        if (Array.isArray(mtrReportRequest.salesPerson) && mtrReportRequest.salesPerson.length === 0) {
            return [];
        }

        var salesPerson;
        if (Array.isArray(mtrReportRequest.salesPerson)) {
            salesPerson = mtrReportRequest.salesPerson.join(',');
            mtrReportRequest.salesPerson = salesPerson;
        }


        let uri = `/monthly-territory-review/${mtrReportRequest.viewType}?isCroView=${isCroView}`;

        try {
            const { data } = await httpRequestHandler.post(
                uri, mtrReportRequest
            );
            return data;
        } catch (error) {
            return [];
        }
    };



    generateCSV = async (
        salesperson,
        month,
        year,
        view = 'NOT-SET',
        brokerCode = 'NOT-SET'
    ) => {
        if (salesperson === undefined) return [];

        try {
            const { data } = await httpRequestHandler.get(
                `/monthly-territory-review/export/${salesperson}/${year}/${month}?view=${view}&brokerCode=${brokerCode}`
            );

            const link = document.createElement('a');

            link.href = data.blobUri;
            link.download = data.blobName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            return [];
        }
    };

    getContractsByRooftopId = async (rooftopId, year, month) => {
        try {
            const { data } = await httpRequestHandler.get(
                `/monthly-territory-review/contracts/${rooftopId}/${year}/${month}`
            );
            return data;
        } catch (error) {
            return [];
        }
    };


    //#endregion

    /**
     * @param {import('axios').AxiosError} error
     * @returns {boolean}
     */
    isCancelError = error => isCancel(error);
}

const claimService = new InsightQoreService(cacheService);
claimService.setSession(claimService.getAccessToken());

export default claimService;
